import { useMemberTimeZone } from '../MemberTimeZoneContext'
import RacquetFinishSummary from './RacquetFinishSummary'
import {
  beginRacquetCompletion,
  hasRacquetCompletion,
  consumeRacquetCompletion
} from '../lib/racquetCompletionJourney'
import { useRacquetCardDraft } from '../lib/useRacquetCardDraft'
import {
  createCardDraft,
  cardDraftOperation,
  cardDraftFingerprint
} from '../lib/racquetCardDraft'
import RacquetModal from './RacquetModal'
import { ArrowLeft } from 'lucide-react'
import LetsPlayLoading from './LetsPlayLoading'
import RacquetDirectCard, {
  RacquetServiceSetup,
  RacquetScoreBreakdown
} from './RacquetDirectCard'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import { useEffect, useMemo, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import { useRacquetMatch } from '../lib/useRacquetMatch'
import {
  downloadRacquetPending,
  racquetPath,
  racquetResponse,
  racquetErrorKey
} from '../lib/racquetApi'
import { observeRacquetOrientation } from '../lib/racquetOrientation'
import {
  isCasual,
  reconstructRacquet,
  lastRacquetScoringEvent,
  pointLabels
} from '../../../shared/racquetScoreDomain.mjs'
import RacquetCorrection from './RacquetCorrection'
import RacquetVoicePanel from './RacquetVoicePanel'
import './racquetPlay.css'
export default function LetsPlayRacquetMatch({ clubCode }) {
  const clubTimeZone = useMemberTimeZone()
  const { memberPortalApiPost: post } = useMemberPortalApi()
  const [voiceEdit, setVoiceEdit] = useState(null)
  const [finishedHere, setFinishedHere] = useState(false)
  const [momentRequested, setMomentRequested] = useState(false)
  const workspace = useRef(null)
  const reviewController = useRef(null),
    applyingPreview = useRef(null),
    localFinishFailed = useRef(false)
  useEffect(() => () => reviewController.current?.abort(), [])
  const { matchId } = useParams(),
    { locale, t: translate } = useOptionalPrivanaI18n(),
    t = (k) => translate(`racquetPlay.${k}`),
    navigate = useNavigate()
  const {
    model: m,
    snapshot: q,
    error,
    online,
    saving,
    submit,
    retryEvent,
    queue,
    sync,
    discard
  } = useRacquetMatch(clubCode, matchId)
  const card = useRacquetCardDraft(clubCode, matchId, m, q, retryEvent)
  const [tab, setTab] = useState('match'),
    [naturalLandscape, setNatural] = useState(false),
    [swapped, setSwapped] = useState(false),
    [viewId, setView] = useState(() => crypto.randomUUID()),
    [panel, setPanel] = useState(null),
    [preview, setPreview] = useState(null),
    [reviewing, setReviewing] = useState(false),
    [reviewError, setReviewError] = useState(false),
    [entryEdit, setEntryEdit] = useState(null),
    [ack, setAck] = useState(false),
    [replacementAck, setReplacementAck] = useState(false),
    [discardAck, setDiscardAck] = useState(false),
    [notice, setNotice] = useState(false),
    [early, setEarly] = useState(false),
    [continueLive, setContinueLive] = useState(false)
  useEffect(() => {
    setPreview(null)
    setReviewing(false)
    setReviewError(false)
    setPanel(null)
    setVoiceEdit(null)
    setTab('match')
    setView(crypto.randomUUID())
    applyingPreview.current = null
    localFinishFailed.current = false
    setFinishedHere(false)
    setMomentRequested(false)
    reviewController.current?.abort()
  }, [card.scopeKey])
  useEffect(() => {
    if (
      [
        'session_saved',
        'completed',
        'retired',
        'abandoned',
        'unfinished'
      ].includes(m?.state.status)
    )
      setTab('match')
  }, [m?.state.status])
  useEffect(() => {
    if (
      tab === 'match' &&
      [
        'session_saved',
        'completed',
        'retired',
        'abandoned',
        'unfinished'
      ].includes(m?.state.status) &&
      workspace.current
    )
      workspace.current.scrollTop = 0
  }, [m?.state.status, tab])
  useEffect(
    () =>
      observeRacquetOrientation((value) => {
        setNatural(value)
      }),
    []
  )
  useEffect(() => {
    setView(crypto.randomUUID())
  }, [swapped])
  useEffect(() => {
    if (continueLive && !card.dirty) {
      setContinueLive(false)
      setTab('match')
      setPanel(null)
    }
  }, [continueLive, card.dirty])
  useEffect(() => {
    if (!panel && !preview) return
    const previous = document.activeElement,
      dialog = document.querySelector('.rp-dialog')
    if (!dialog) return
    const controls = () =>
      [
        ...dialog.querySelectorAll('button,input,select,textarea,a[href]')
      ].filter((e) => !e.disabled && !e.hidden)
    dialog.setAttribute('data-mp-static-focus', '')
    dialog.setAttribute('tabindex', '-1')
    dialog.scrollTop = 0
    dialog.focus({ preventScroll: true })
    const key = (e) => {
      if (e.key === 'Escape') {
        reviewController.current?.abort()
        reviewController.current = null
        setVoiceEdit(null)
        setPanel(null)
        setPreview(null)
        if (card.dirty) setTab('card')
      }
      if (e.key === 'Tab') {
        const list = controls(),
          first = list[0],
          last = list.at(-1)
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last?.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first?.focus()
        }
      }
    }
    dialog.addEventListener('keydown', key)
    return () => {
      dialog.removeEventListener('keydown', key)
      previous?.focus?.()
    }
  }, [panel, preview])
  useEffect(() => {
    if (momentRequested && q?.saved && m && !q.blocked && ['completed', 'session_saved'].includes(m.state.status)) {
      setMomentRequested(false)
      consumeRacquetCompletion(card.scopeKey)
      navigate(`/${clubCode}/lets-play/activities/${m.id}/moment?journey=racquet`)
    }
  }, [momentRequested, q?.saved, m, clubCode, navigate, card.scopeKey])
  useEffect(() => {
    if (localFinishFailed.current && !q?.storageError && m?.state.status === 'session_saved') {
      localFinishFailed.current = false
      setPreview(null)
      setNotice(false)
      const event = m.events.at(-1)
      const first = !m.events.slice(0, -1).some(e => e.operation.finishSession || ['finish_session', 'confirm_result'].includes(e.operation.type))
      setFinishedHere(first)
      if (event && first) beginRacquetCompletion(card.scopeKey, event.id)
      else consumeRacquetCompletion(card.scopeKey)
    }
  }, [q?.storageError, m?.state.status])
  const names = useMemo(
    () => new Map(m?.participants.map((p) => [p.id, p.display_name]) || []),
    [m?.participants]
  )
  if (!m || m.id !== matchId)
    return (
      <section className="rp">
        {error ? (
          <p role="alert">{t('error')}</p>
        ) : (
          <LetsPlayLoading
            label={t('restoringMatch')}
            detail={t('restoringDetail')}
          />
        )}
        {error && (
          <button onClick={() => location.reload()}>{t('retry')}</button>
        )}
      </section>
    )
  const s = m.state,
    casual = isCasual(s),
    teamName = (team) => team.players.map((id) => names.get(id)).join(' & '),
    teams = swapped ? [...s.teams].reverse() : s.teams,
    displayTeams = teams.map((t) => t.id),
    labels = pointLabels(s),
    pending = Boolean(q?.events.length),
    blocked = Boolean(q?.blocked),
    terminal = [
      'session_saved',
      'completed',
      'retired',
      'abandoned',
      'unfinished'
    ].includes(s.status),
    canEdit = m.permissions.canScore && !blocked,
    disabled =
      !canEdit ||
      s.status !== 'in_progress' ||
      Boolean(panel) ||
      Boolean(preview) ||
      q?.storageError,
    finishing = Boolean(preview && (preview.operation.type === 'finish_session' || preview.operation.finishSession)),
    replacementConsent = Boolean(preview?.operation.type === 'direct_score' && m.events.length),
    wide =
      naturalLandscape && tab === 'match' && !terminal && s.status !== 'paused'
  const action = (operation, voice = {}) => {
    if (card.dirty && operation.type !== 'direct_score') {
      setPanel('transition')
      return null
    }
    setNotice(false)
    try {
      const firstCompletion = !terminal && !m.events.some(e => e.operation.finishSession || ['finish_session', 'confirm_result'].includes(e.operation.type))
      const accepted = submit(operation, voice)
      if (operation.finishSession || ['finish_session', 'confirm_result'].includes(operation.type)) {
        setFinishedHere(firstCompletion)
        if (firstCompletion) beginRacquetCompletion(card.scopeKey, accepted.id)
        else consumeRacquetCompletion(card.scopeKey)
      }
      return accepted
    } catch (e) {
      if (e.code === 'queue_storage_failed' && (operation.type === 'finish_session' || operation.finishSession)) localFinishFailed.current = true
      setNotice(e.code || 'error')
      return null
    }
  }
  const showPreview = async (p) => {
    p = {
      ...p,
      replacement: p.operation.type === 'direct_score' && m.events.length > 0,
      reviewedHistory: JSON.stringify(m.events.map((e) => e.id))
    }
    reviewController.current?.abort()
    setPreview(p)
    setAck(false)
    setPanel(null)
    setReviewError(false)
    setReviewing(false)
    const priorVoice = voiceEdit || card.draft?.voiceProposal
    if (priorVoice) {
      setReviewing(true)
      const controller = new AbortController()
      reviewController.current = controller
      const timer = setTimeout(() => controller.abort(), 15000)
      try {
        const requested = p
        const reviewed = await post(
          racquetPath(clubCode, `/matches/${m.id}/voice-review`),
          { token: priorVoice.token, viewId, operation: p.operation },
          { signal: controller.signal }
        ).then(racquetResponse)
        p = { ...requested, ...reviewed }
        if (
          controller.signal.aborted ||
          reviewController.current !== controller
        )
          return
      } catch (e) {
        if (reviewController.current === controller) setReviewError(true)
        return
      } finally {
        clearTimeout(timer)
        if (reviewController.current === controller) setReviewing(false)
      }
      setVoiceEdit(null)
      if (card.dirty) card.change({ voiceProposal: p })
    }
    setPreview(p)
    setAck(false)
    setReplacementAck(false)
    setPanel(null)
  }
  const reviewFinish = () => {
    if (card.error === 'draft_unreadable') {
      setTab('card')
      return
    }
    if (card.draft?.pendingEventId) {
      setTab('card')
      return
    }
    try {
      const result = card.dirty
        ? cardDraftOperation(card.draft, m, {
            finish: true,
            reason: t('directEntryReason'),
            review: true
          })
        : null
      const operation = result?.operation || { type: 'finish_session' }
      showPreview({
        operation,
        before: s,
        after:
          result?.after ||
          reconstructRacquet(m.initialState, [
            ...m.events,
            { id: 'preview', operation }
          ]),
        revision: m.revision,
        viewId,
        cardDraft: card.dirty,
        draftFingerprint: card.dirty ? cardDraftFingerprint(card.values) : null,
        replacement: card.dirty && m.events.length > 0
      })
    } catch (e) {
      card.setError(e.code || e.message)
      setTab('card')
      setPanel(null)
    }
  }
  const openAction = (name) => {
    if (card.dirty) {
      setPanel('transition')
    } else setPanel(name)
  }
  const goBack = () => {
    if (
      terminal &&
      tab === 'card' &&
      (!card.dirty || card.values?.pendingEventId)
    )
      setTab('match')
    else if (terminal && !card.dirty) navigate(`/${clubCode}/lets-play`)
    else if (card.dirty) setPanel('leaveDraft')
    else navigate(`/${clubCode}/lets-play/${m.rules.sport}`)
  }
  const describe = (state) =>
    `${t(state.status === 'session_saved' ? 'finishedOutcome' : state.status)} · ${state.server ? `${t('serving')}: ${names.get(state.server) || t('serviceUnknown')}` : t('serviceUnknown')}`
  const last = lastRacquetScoringEvent(m.events),
    undoable =
      last &&
      [
        'direct_score',
        'configure_service',
        'rally',
        'correct_current',
        'correct_games',
        'replace_last_rally',
        'award_game',
        'summary_segment',
        'replace_segment'
      ].includes(last.operation.type) &&
      !terminal
  const resultDate = m.startedAt || m.completedAt || m.events.at(-1)?.created_at
  const previewStale =
    preview &&
    (preview.revision !== m.revision ||
      preview.viewId !== viewId ||
      (preview.reviewedHistory &&
        preview.reviewedHistory !==
          JSON.stringify(m.events.map((e) => e.id))) ||
      (preview.draftFingerprint &&
        preview.draftFingerprint !== cardDraftFingerprint(card.values)))
  const status = blocked
    ? 'conflict'
    : q?.lastFailure && !saving
      ? 'saveFailed'
    : q?.storageError
      ? 'storageError'
      : !online
        ? 'pending'
        : saving
          ? 'saving'
          : pending
            ? 'pendingOnline'
            : 'saved'
  return (
    <RacquetViewport wide={wide}>
      <section
        ref={workspace}
        className={`rp rp-live ${wide ? 'is-landscape' : ''} ${tab === 'match' && !terminal && s.status === 'in_progress' ? 'is-scoring is-live' : 'is-entry is-editor'}`}
      >
        <header className="rp-live-header">
          <button onClick={goBack}>
            <ArrowLeft size={17} aria-hidden="true" /> {t('back')}
          </button>
          <div>
            <strong title={m.court.name} aria-label={m.court.name}>
              {m.court.name}
            </strong>
            <small>
              {t(m.rules.sport)} ·{' '}
              {t(s.teams[0].players.length === 2 ? 'doubles' : 'singles')}
            </small>
          </div>
          <span className="rp-sync" role="status">
            {t(
              status === 'conflict'
                ? 'conflictStatus'
                : status === 'storageError'
                  ? 'storageStatus'
                  : status
            )}
          </span>
        </header>
        {(notice || error) && (
          <p className="rp-notice" role="alert">
            {t(racquetErrorKey(notice))}{' '}
            {notice === 'draft_action_required' ? (
              <>
                <button onClick={() => setTab('card')}>
                  {t('keepEditing')}
                </button>
                <button
                  onClick={() => {
                    if (card.discard()) setNotice(false)
                  }}
                >
                  {t('discardChanges')}
                </button>
              </>
            ) : (
              <button onClick={sync}>{t('retry')}</button>
            )}
          </p>
        )}
        {(blocked || q?.storageError) && (
          <section className="rp-notice" role="alert">
            <p>{t(!blocked || q.blocked === 'queue_unreadable' ? 'storageError' : q.blocked === 'event_rejected' ? 'rejectedSave' : q.blocked === 'acknowledgement_invalid' ? 'unverifiedSave' : 'conflict')}</p>
            <p>{describe(s)}</p>
            <button onClick={() => downloadRacquetPending(queue)}>
              {t('export')}
            </button>
            {blocked && (
              <>
                <label className="rp-check">
                  <input
                    type="checkbox"
                    checked={discardAck}
                    onChange={(e) => setDiscardAck(e.target.checked)}
                  />
                  {t('discardConfirm')}
                </label>
                <button
                  disabled={!discardAck || saving}
                  onClick={async () => {
                    try {
                      card.resolveAfterQueueReview()
                      await discard()
                      setDiscardAck(false)
                    } catch {
                      setNotice(true)
                    }
                  }}
                >
                  {t('useServer')}
                </button>
              </>
            )}
            <button onClick={sync}>{t('retry')}</button>
          </section>
        )}
        {!m.permissions.canScore && <p>{t('readOnly')}</p>}
        {!terminal && (
          <nav
            className="rp-tabs mp-golf-score-tabs"
            aria-label={t('scoringMode')}
          >
            {['match', 'card'].map((key) => (
              <button
                key={key}
                aria-pressed={tab === key}
                onClick={() => {
                  if (key === 'match' && card.dirty) setPanel('transition')
                  else {
                    setTab(key)
                    setNotice(false)
                  }
                }}
              >
                {t(key === 'match' ? 'live' : 'finalScores')}
              </button>
            ))}
          </nav>
        )}
        {terminal && tab !== 'card' && (
          <section className="rp-result rp-completion" aria-busy={saving}>
            <span className="rp-completion-mark" aria-hidden="true">✧</span>
            <small>{t('letsPlayBrand')}</small>
            <h1>{t('finishedOutcome')}</h1>
            <p>{m.court.name} · {t(m.rules.sport)}{resultDate && ` · ${new Date(resultDate).toLocaleDateString(locale, { timeZone:m.timeZone || clubTimeZone, day: 'numeric', month: 'long', year: 'numeric' })}`}</p>
            <RacquetFinishSummary state={s} t={t} teamName={teamName} />
            <p className="rp-result-status" role="status">{t(blocked ? 'conflictStatus' : q?.storageError ? 'storageStatus' : q?.lastFailure && !saving ? 'saveFailed' : pending ? (online ? 'savingResult' : 'offlineResult') : 'syncedResult')}</p>
            {q?.lastFailure && !saving && !blocked && <button onClick={sync}>{t('retry')}</button>}
            <button className="rp-primary" disabled={momentRequested || blocked || Boolean(q?.storageError)} onClick={() => {
              const offerMoment = finishedHere && m.permissions.canScore && ['session_saved', 'completed'].includes(s.status) && hasRacquetCompletion(card.scopeKey, m.events)
              if (offerMoment) setMomentRequested(true)
              else { consumeRacquetCompletion(card.scopeKey); navigate(`/${clubCode}/lets-play`) }
            }}>{t(momentRequested ? 'momentWaiting' : 'continue')}</button>
            <button onClick={() => { consumeRacquetCompletion(card.scopeKey); setMomentRequested(false); navigate(`/${clubCode}/lets-play`) }}>{t('done')}</button>
            <button onClick={() => setTab('card')}>{t('scorecard')}</button>
          </section>
        )}
        {s.status === 'paused' && (
          <section className="rp-notice">
            <h2>{t('paused')}</h2>
            {canEdit && (
              <button
                className="rp-primary"
                onClick={() => action({ type: 'resume' })}
              >
                {t('resume')}
              </button>
            )}
          </section>
        )}
        {s.status === 'awaiting_confirmation' && (
          <section className="rp-result">
            <h2>{t('resultReady')}</h2>
            <p>
              {teamName(s.teams.find((team) => team.id === s.winner))} ·{' '}
              {s.segments.map((seg) => seg.score.join('–')).join(' · ')}
            </p>
            {canEdit && (
              <button
                className="rp-primary"
                onClick={() => action({ type: 'confirm_result' })}
                disabled={pending || saving}
              >
                {t('confirmResult')}
              </button>
            )}
          </section>
        )}
        {tab === 'match' && !terminal && s.status !== 'paused' && (
          <>
            <div className="rp-totals">
              {t(['tennis', 'padel'].includes(m.rules.sport) ? 'set' : 'game')}{' '}
              {s.segments.length + 1}
              {['tennis', 'padel'].includes(m.rules.sport) && (
                <>
                  {' '}
                  · {t('games')} {s.games.join('–')}
                </>
              )}
              {s.tieBreak && <strong>{t('tieBreak')}</strong>}
            </div>
            <div className="rp-score-grid">
              {teams.map((team) => {
                const i = s.teams.findIndex((t) => t.id === team.id),
                  serving = team.players.includes(s.server)
                return (
                  <button
                    key={team.id}
                    data-team-colour={i === 0 ? 'emerald' : 'blue'}
                    className={`rp-score-panel ${serving ? 'is-serving' : ''}`}
                    disabled={disabled}
                    onClick={() => {
                      if (!s.server || s.serviceBox === null)
                        setPanel('service')
                      else action({ type: 'rally', teamId: team.id })
                    }}
                  >
                    <span className="rp-team-title" title={teamName(team)}>
                      {teamName(team)}
                    </span>
                    <span className="rp-serving">
                      {serving
                        ? `● ${team.players.length === 1 ? t('serving') : names.get(s.server)}${m.rules.sport === 'pickleball' ? ` · ${s.serverNumber}` : ''}`
                        : ' '}
                    </span>
                    <strong
                      className="rp-score"
                      style={{
                        '--rp-score-width': `${135 / Math.max(2, String(labels[i]).length)}cqw`
                      }}
                    >
                      {labels[i]}
                    </strong>
                    <span className="rp-action">
                      {m.rules.sport === 'pickleball'
                        ? t('rallyWon')
                        : `+ ${t('pointAction')}`}
                    </span>
                  </button>
                )
              })}
            </div>
            <div className="rp-toolbar">
              <button
                disabled={!canEdit || !undoable || Boolean(preview)}
                onClick={() => action({ type: 'undo', targetEventId: last.id })}
              >
                {t('undo')}
              </button>
              {wide && (
                <button onClick={() => setSwapped((v) => !v)}>
                  {t('swap')}
                </button>
              )}
              {wide && casual && canEdit && (
                <button className="rp-finish-compact" onClick={reviewFinish}>
                  {t('finishSession')}
                </button>
              )}
              <button onClick={() => setPanel('more')}>
                {t('moreActions')}
              </button>
            </div>
          </>
        )}
        {tab === 'card' && (!terminal || tab === 'card') && (
          <>
            <h1 className="rp-entry-heading">{t('enterScores')}</h1>
            {casual ? (
              terminal && !card.dirty ? (
                <>
                  <RacquetScoreBreakdown state={s} t={t} teamName={teamName} />
                  {canEdit && (
                    <button
                      disabled={pending || saving || Boolean(q?.storageError)}
                      onClick={() => {
                        setEntryEdit({ after: s })
                        card.replace(createCardDraft(m))
                        setVoiceEdit(null)
                        setPanel('direct')
                      }}
                    >
                      {t('edit')}
                    </button>
                  )}
                </>
              ) : (
                panel !== 'direct' && (
                  <RacquetDirectCard
                    match={m}
                    editor={card}
                    t={t}
                    teamName={teamName}
                    viewId={viewId}
                    onPreview={showPreview}
                    disabled={
                      !canEdit || Boolean(preview) || Boolean(q?.storageError)
                    }
                  />
                )
              )
            ) : (
              <>
                <p>{t(m.rules.id)}</p>
                <p>{describe(s)}</p>
                <div className="rp-segments">
                  {s.segments.map((seg, i) => (
                    <div key={i}>
                      <strong>
                        {t(
                          ['tennis', 'padel'].includes(m.rules.sport)
                            ? 'sets'
                            : 'games'
                        )}{' '}
                        {i + 1}
                      </strong>
                      <span>{seg.score.join(' – ')}</span>
                      <small>
                        {t(
                          seg.provenance === 'summary'
                            ? 'summaryOnly'
                            : 'rallyHistory'
                        )}
                      </small>
                    </div>
                  ))}
                </div>
                {s.summaryOnly && (
                  <p className="rp-notice">{t('summaryNote')}</p>
                )}
                {canEdit && (
                  <div className="rp-options">
                    <button
                      disabled={!undoable}
                      onClick={() =>
                        action({ type: 'undo', targetEventId: last.id })
                      }
                    >
                      {t('undo')}
                    </button>
                    <button
                      disabled={
                        ![
                          'in_progress',
                          'awaiting_confirmation',
                          'completed'
                        ].includes(s.status) ||
                        pending ||
                        Boolean(preview)
                      }
                      onClick={() => openAction('correction')}
                    >
                      {t('correct')}
                    </button>
                  </div>
                )}
              </>
            )}
            <button
              className="rp-history-control"
              onClick={() => setPanel('history')}
            >
              {t('history')}
            </button>
            {terminal && <button onClick={goBack}>{t('backToResult')}</button>}
            {panel === 'history' && (
              <RacquetModal>
                <section
                  className="rp-dialog"
                  role="dialog"
                  aria-modal="true"
                  aria-label={t('history')}
                >
                  <h2>{t('history')}</h2>
                  <ol className="rp-history">
                    {[...m.events].reverse().map((e) => (
                      <li key={e.id}>
                        <strong>{t(`operation_${e.operation.type}`)}</strong>
                        <span>
                          {e.operation.teamId
                            ? teamName(
                                s.teams.find(
                                  (team) => team.id === e.operation.teamId
                                )
                              )
                            : ''}{' '}
                          {e.operation.points?.join('–') ||
                            e.operation.score?.join('–')}
                        </span>
                        <small>
                          {e.created_at
                            ? new Date(e.created_at).toLocaleString(locale, {timeZone:m.timeZone || clubTimeZone})
                            : t('saving')}{' '}
                          ·{' '}
                          {m.participants.find(
                            (p) => p.member_id === e.actor_member_id
                          )?.display_name || ''}
                        </small>
                        {e.operation.reason && <p>{e.operation.reason}</p>}
                        {e.voice_proposal && (
                          <blockquote>{e.voice_proposal.transcript}</blockquote>
                        )}
                      </li>
                    ))}
                  </ol>
                  <button onClick={() => setPanel(null)}>{t('back')}</button>
                </section>
              </RacquetModal>
            )}
            {!casual && (
              <details>
                <summary>{t('source')}</summary>
                <a href={m.rules.source?.url} target="_blank" rel="noreferrer">
                  {m.rules.source?.body} · {m.rules.source?.edition}
                </a>
              </details>
            )}
            {!terminal && !casual && canEdit && (
              <section className="rp-early">
                <button onClick={() => setEarly((v) => !v)}>
                  {t('endEarly')}
                </button>
                {early && (
                  <>
                    <p>{t('earlyNote')}</p>
                    {[
                      ['retire', 'retire'],
                      ['abandon', 'abandon'],
                      ['unfinished', 'finishUnfinished']
                    ].map(([type, label]) => (
                      <button
                        key={type}
                        disabled={pending || Boolean(preview)}
                        onClick={() => {
                          setPreview({
                            operation: { type },
                            before: s,
                            after: {
                              ...s,
                              status:
                                type === 'retire'
                                  ? 'retired'
                                  : type === 'abandon'
                                    ? 'abandoned'
                                    : 'unfinished',
                              winner: null
                            },
                            revision: m.revision,
                            viewId
                          })
                        }}
                      >
                        {t(label)}
                      </button>
                    ))}
                  </>
                )}
              </section>
            )}
          </>
        )}
        {casual && !terminal && canEdit && !wide && tab === 'match' && (
          <button className="rp-primary" onClick={reviewFinish}>
            {t('finishSession')}
          </button>
        )}
        {card.storageError && (
          <p className="rp-draft-status" role="status">
            {t('draftStorageError')}
          </p>
        )}
        {panel === 'transition' && (
          <RacquetModal>
            <section
              className="rp-dialog"
              role="dialog"
              aria-modal="true"
              aria-label={t('unappliedChanges')}
            >
              <h2>{t('unappliedChanges')}</h2>
              <button
                disabled={Boolean(card.values?.pendingEventId)}
                onClick={() => {
                  try {
                    const result = cardDraftOperation(card.values, m, {
                      reason: t('directEntryReason'),
                      review: true
                    })
                    showPreview({
                      ...result,
                      before: s,
                      revision: m.revision,
                      viewId,
                      cardDraft: true,
                      continueLive: true,
                      draftFingerprint: cardDraftFingerprint(card.values)
                    })
                  } catch (e) {
                    card.setError(e.code || e.message)
                    setPanel(null)
                    setTab('card')
                  }
                }}
              >
                {t('applyContinue')}
              </button>
              <button
                disabled={Boolean(card.values?.pendingEventId)}
                onClick={() => {
                  if (card.discard()) {
                    setPanel(null)
                    setTab('match')
                    setNotice(false)
                  }
                }}
              >
                {t('discardContinue')}
              </button>
              <button
                onClick={() => {
                  setPanel(null)
                  setTab('card')
                }}
              >
                {t('keepEditing')}
              </button>
            </section>
          </RacquetModal>
        )}
        {panel === 'service' && (
          <RacquetModal>
            <section
              className="rp-dialog"
              role="dialog"
              aria-modal="true"
              aria-label={t('serviceSetup')}
            >
              <h2>{t('serviceSetup')}</h2>
              {!s.server || s.serviceBox !== null ? (
                <RacquetServiceSetup
                  state={s}
                  t={t}
                  names={names}
                  disabled={!canEdit || card.dirty}
                  onSubmit={(operation) => {
                    if (action(operation)) setPanel(null)
                  }}
                />
              ) : (
                <>
                  <p>{t('boxRequired')}</p>
                  {['left', 'right'].map((box) => (
                    <button
                      key={box}
                      onClick={() => {
                        if (action({ type: 'service_box', box })) setPanel(null)
                      }}
                    >
                      {t(box)}
                    </button>
                  ))}
                </>
              )}
              <button onClick={() => setPanel(null)}>{t('cancel')}</button>
            </section>
          </RacquetModal>
        )}
        {panel === 'more' && (
          <RacquetModal>
            <section
              className="rp-dialog"
              role="dialog"
              aria-modal="true"
              aria-label={t('moreActions')}
            >
              <h2>{t('moreActions')}</h2>
              <button
                disabled={!canEdit || pending || saving}
                onClick={() => setPanel('voice')}
              >
                {t('voice')}
              </button>
              <button
                onClick={() => {
                  setSwapped((v) => !v)
                  setPanel(null)
                }}
              >
                {t('swap')}
              </button>
              <button
                disabled={!canEdit}
                onClick={() => setPanel(casual ? 'service' : 'correction')}
              >
                {t('changeService')}
              </button>
              <button
                disabled={!canEdit}
                onClick={() => {
                  if (action({ type: 'pause' })) setPanel(null)
                }}
              >
                {t('pause')}
              </button>
              {casual && canEdit && (
                <button onClick={reviewFinish}>{t('finishSession')}</button>
              )}
              <button
                onClick={() => {
                  setPanel(null)
                  setTab('card')
                }}
              >
                {t('finalScores')}
              </button>
              <button onClick={() => setPanel(null)}>{t('cancel')}</button>
            </section>
          </RacquetModal>
        )}
        {panel === 'leaveDraft' && (
          <RacquetModal>
            <section
              className="rp-dialog"
              role="dialog"
              aria-modal="true"
              aria-label={t('unappliedChanges')}
            >
              <h2>{t('unappliedChanges')}</h2>
              <p>{t('draftLeaveNote')}</p>
              <button
                onClick={() => {
                  setPanel(null)
                  setTab('card')
                }}
              >
                {t('keepEditing')}
              </button>
              <button
                onClick={() => {
                  if (card.discard()) {
                    setPanel(null)
                    navigate(`/${clubCode}/lets-play/${m.rules.sport}`)
                  }
                }}
              >
                {t('discardAndLeave')}
              </button>
              <button
                disabled={card.storageError}
                onClick={() => {
                  setPanel(null)
                  navigate(`/${clubCode}/lets-play/${m.rules.sport}`)
                }}
              >
                {t('keepDraftAndLeave')}
              </button>
            </section>
          </RacquetModal>
        )}
        {panel === 'direct' && (
          <RacquetModal>
            <section
              className="rp-dialog"
              role="dialog"
              aria-modal="true"
              aria-label={t('correct')}
            >
              <RacquetDirectCard
                match={m}
                editor={card}
                t={t}
                teamName={teamName}
                viewId={viewId}
                onPreview={showPreview}
                disabled={!canEdit}
              />
              <button
                onClick={() => {
                  setPanel(null)
                  setVoiceEdit(null)
                  if (card.dirty) setTab('card')
                }}
              >
                {t('cancel')}
              </button>
            </section>
          </RacquetModal>
        )}
        {panel === 'correction' && (
          <RacquetModal>
            <RacquetCorrection
              match={m}
              t={t}
              viewId={viewId}
              onClose={() => {
                reviewController.current?.abort()
                reviewController.current = null
                setPanel(null)
                setVoiceEdit(null)
              }}
              onPreview={showPreview}
            />
          </RacquetModal>
        )}
        {panel === 'voice' && (
          <RacquetModal>
            <RacquetVoicePanel
              clubCode={clubCode}
              match={m}
              t={t}
              locale={locale}
              viewId={viewId}
              displayTeams={displayTeams}
              onClose={() => setPanel(null)}
              onPreview={showPreview}
            />
          </RacquetModal>
        )}
        {preview && (
          <RacquetModal>
            <section
              className={`rp-dialog ${finishing ? 'rp-finish-review' : ''}`}
              role="dialog"
              aria-modal="true"
              aria-label={t(finishing ? 'finishQuestion' : 'preview')}
            >
              <h2>{t(finishing ? 'finishQuestion' : 'preview')}</h2>
              {q?.storageError && <p role="alert">{t('storageError')} <button onClick={sync}>{t('retry')}</button></p>}
              {reviewing && <p role="status">{t('preparingReview')}</p>}
              {reviewError && <p role="alert">{t('reviewPreparationError')}</p>}
              {!finishing && <>
                <p>{t(`operation_${preview.operation.type}`)}</p>
                {preview.transcript && <p><strong>{t('transcript')}</strong>: {preview.transcript}</p>}
                {preview.operation.type !== 'direct_score' && <>
                  <h3>{t('before')}</h3><p>{describe(preview.before)}</p>
                  <RacquetScoreBreakdown state={preview.before} t={t} teamName={teamName} />
                </>}
                <h3>{t('after')}</h3>
              </>}
              <RacquetFinishSummary state={preview.after} t={t} teamName={teamName} />
              {replacementConsent && <label className="rp-check">
                <input type="checkbox" checked={replacementAck} disabled={reviewing || reviewError || previewStale} onChange={e => setReplacementAck(e.target.checked)} />
                {t('directHistoryAck')}
              </label>}
              {preview.operation.type === 'replace_segment' && (
                <p>{t('discardFollowing')}</p>
              )}
              {preview.repair && (
                <>
                  <p>
                    {t('repair')}: {preview.repair.original} →{' '}
                    {preview.repair.replacement}
                  </p>
                  <label className="rp-check">
                    <input
                      type="checkbox"
                      checked={ack}
                      disabled={reviewing || reviewError}
                      onChange={(e) => setAck(e.target.checked)}
                    />
                    {t('ackRepair')}
                  </label>
                </>
              )}
              {previewStale && (
                <p role="alert">
                  {t(preview.token ? 'staleVoice' : 'reviewChanged')}
                </p>
              )}
              {!finishing && !['finish_session', 'retire', 'abandon', 'unfinished'].includes(
                preview.operation.type
              ) && (
                <button
                  onClick={() => {
                    reviewController.current?.abort()
                    reviewController.current = null
                    setReviewing(false)
                    setReviewError(false)
                    setVoiceEdit(preview.token ? preview : null)
                    if (preview.operation.type === 'direct_score') {
                      setEntryEdit(preview)
                      if (!card.dirty)
                        card.replace({
                          ...createCardDraft({ ...m, state: preview.after }),
                          ...(preview.token ? { voiceProposal: preview } : {})
                        })
                    }
                    setPanel(
                      preview.operation.type === 'direct_score'
                        ? 'direct'
                        : 'correction'
                    )
                    setPreview(null)
                  }}
                >
                  {t('edit')}
                </button>
              )}
              <button
                onClick={() => {
                  reviewController.current?.abort()
                  reviewController.current = null
                  setReviewing(false)
                  setReviewError(false)
                  setPreview(null)
                  if (card.dirty) setTab('card')
                }}
              >
                {t(finishing ? 'keepEditing' : 'cancel')}
              </button>
              <button
                className="rp-primary"
                disabled={
                  Boolean(q?.storageError) ||
                  previewStale || reviewing || reviewError ||
                  Boolean(preview.repair && !ack) ||
                  Boolean(replacementConsent && !replacementAck) ||
                  Boolean(applyingPreview.current === preview)
                }
                onClick={() => {
                  if (applyingPreview.current === preview) return
                  applyingPreview.current = preview
                  const accepted = action(
                    preview.operation,
                    preview.token
                      ? {
                          voiceToken: preview.token,
                          viewId,
                          repairAcknowledged: ack
                        }
                      : {}
                  )
                  if (!accepted) {
                    applyingPreview.current = null
                    return
                  }
                  if (
                    preview.cardDraft ||
                    (card.dirty && preview.operation.type === 'direct_score')
                  ) {
                    if (!card.draft) card.replace(createCardDraft(m))
                    card.queued(accepted)
                  }
                  if (finishing && !terminal) setFinishedHere(true)
                  if (
                    preview.operation.finishSession ||
                    preview.operation.type === 'finish_session'
                  )
                    setTab('match')
                  if (preview.continueLive) setContinueLive(true)
                  setPreview(null)
                }}
              >
                {t(finishing ? 'saveFinish' : 'apply')}
              </button>
            </section>
          </RacquetModal>
        )}
      </section>
    </RacquetViewport>
  )
}

// Keep one mounted tree across rotation. Only modal sheets use a body portal.
function RacquetViewport({ wide, children }) {
  const host = useRef(null)
  useEffect(() => {
    const resize = () => {
      const node = host.current
      if (!node) return
      const viewport = window.visualViewport
      const bottom =
        (viewport?.height || window.innerHeight) + (viewport?.offsetTop || 0)
      node.style.setProperty(
        '--rp-available-height',
        `${Math.max(0, bottom - node.getBoundingClientRect().top)}px`
      )
    }
    const observer = new ResizeObserver(resize)
    if (host.current?.parentElement)
      observer.observe(host.current.parentElement)
    window.addEventListener('resize', resize)
    window.visualViewport?.addEventListener('resize', resize)
    window.visualViewport?.addEventListener('scroll', resize)
    resize()
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', resize)
      window.visualViewport?.removeEventListener('resize', resize)
      window.visualViewport?.removeEventListener('scroll', resize)
    }
  }, [])
  useEffect(() => {
    if (!wide) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [wide])
  return (
    <div ref={host} className="rp-viewport">
      {children}
    </div>
  )
}
